import { injectable, inject } from 'inversify';
import { ITrackedWinner, WinnerTrackedFunc } from '@/components/game/_domain/modules/winners-tracker-service/models';
import { Subject } from 'rxjs/Subject';
import { Subscription } from 'rxjs/Subscription';
import { ConfigurationKey, IConfigurationService, IGameConfigurationService } from '@/app-modules/config';
import { PrizeHelper } from '@/components/game/_domain/helpers/prize-helper';
import { SERVICE_IDENTIFIERS } from '@/app-modules/_ioc/service-identifiers';
import { ISweetAlertService } from '@/app-modules/sweet-alert-service';
import { ILocalisationService } from '@/app-modules/localisation-service';
import { SweetAlertOptions } from 'sweetalert2';
import { Lang } from 'vuejs-localization';
import { container } from '@/app-modules/_ioc';
import { IPersistedStore } from '@/app-modules/store/persisted-store';
import { UserPreferences } from '@/app-modules/store/modules/user-preferences';
import { SoundService, ISoundService, Sounds } from '@/app-modules/sound-service';
import { GameLocalisation } from '@/components/game/_domain/modules/game-localisation';

const TRACKED_WINNERS_LIST_CLEANER_INTERVAL_MILLISECONDS: number = 300000;  // five minutes

interface IWinnersTrackerService {
  trackWinners(...winners: ITrackedWinner[]): void;
  getAllTrackedWinners(): ITrackedWinner[];
  onWinnerTracked(func: WinnerTrackedFunc): Subscription;
  freeze(): void;
  unfreeze(): void;
  isFrozen(): boolean;
  copyTrackedWinnerToClipboard(winner: ITrackedWinner): void;
  dispose(): void;
}

@injectable()
class WinnersTrackerService implements IWinnersTrackerService {
  private readonly $lang: Lang;
  private readonly maxTrackedWinnersListSize: number;
  private readonly trackedWinnersList: ITrackedWinner[] = [];
  private readonly frozenTrackedWinnersList: ITrackedWinner[] = [];
  private isWinnersTrackingFrozen: boolean = false;
  private trackedWinnersListCleanupIntervalTimerHandle: number;
  private onWinnerTrackedObservable = new Subject<ITrackedWinner>();
  public prizeHelper: PrizeHelper = new PrizeHelper();
  private persistedStore = container.get(SERVICE_IDENTIFIERS.IPersistedStore) as IPersistedStore;
  private userPreferences: UserPreferences = this.persistedStore.Get(UserPreferences);
  private soundService: SoundService = container.get(SERVICE_IDENTIFIERS.ISoundService) as ISoundService;
  private winnerCollectorTimeout: number;
  private winnerCollector: ITrackedWinner[] = [];
  private gameLocalisation: GameLocalisation;

  constructor(
    @inject(SERVICE_IDENTIFIERS.IConfigurationService) private readonly configurationService: IConfigurationService,
    @inject(SERVICE_IDENTIFIERS.IGameConfigurationService) private readonly gameConfigurationService: IGameConfigurationService,
    @inject(SERVICE_IDENTIFIERS.ISweetAlertService) private readonly sweetAlertService: ISweetAlertService,
    @inject(SERVICE_IDENTIFIERS.ILocalisationService) private readonly localisationService: ILocalisationService
  ) {
    this.$lang = this.localisationService.getLangInstance();
    this.maxTrackedWinnersListSize = this.getMaxTrackedWinnersListSize();
    this.initialiseTrackedWinnersListCleanupIntervalTimer();
    this.gameLocalisation = new GameLocalisation(this.$lang);
  }

  public trackWinners(...winners: ITrackedWinner[]): void {
    if (winners.length > 0) {
      const trackedWinnersList = this.getTrackedWinnersList();
      trackedWinnersList.push(...winners);
      this.notifyWinnersTracked(winners);
    }
  }

  public getAllTrackedWinners(): ITrackedWinner[] {
    return this.trackedWinnersList;
  }

  public onWinnerTracked(func: WinnerTrackedFunc): Subscription {
    return this.onWinnerTrackedObservable.subscribe(func);
  }

  public freeze(): void {
    this.isWinnersTrackingFrozen = true;
  }

  public unfreeze(): void {
    this.isWinnersTrackingFrozen = false;
    this.trackWinners(...this.frozenTrackedWinnersList);
    this.frozenTrackedWinnersList.splice(0);
  }

  public isFrozen(): boolean {
    return this.isWinnersTrackingFrozen;
  }

  public copyTrackedWinnerToClipboard(winner: ITrackedWinner): void {
    const tempTextAreaElement = document.createElement('textarea');
    try {
      const messageTitle: string = this.$lang.game.winners.popup.title;
      const prize = this.prizeHelper.formatPrize(winner.prizeInPence);

      let gameName: string = '';
      let prizeName: string = '';
      let messageTemplate: string = '';

      if (winner.skinName) {
        messageTemplate = this.$lang.game.winners.popup.message;
        gameName = this.gameLocalisation.getLocalGameName(winner.gameName, winner.skinName);
      } else {
        messageTemplate = this.$lang.game.winners.popup.extendedMessage;
        gameName = winner.gameName + (winner.clubName && winner.clubName !== winner.gameName ? ` (${winner.clubName})` : '');
        prizeName = winner.prizeName ? winner.prizeName : 'Arcade';
      }
      const messageText = messageTemplate.format({
        username: winner.username,
        prize,
        gameName,
        prizeType: prizeName
      });
      tempTextAreaElement.value = messageTitle + ' ' + messageText;
      document.body.appendChild(tempTextAreaElement);
      tempTextAreaElement.select();
      document.execCommand('copy');
    }
    finally {
      document.body.removeChild(tempTextAreaElement);
    }
  }

  public dispose(): void {
    window.clearInterval(this.trackedWinnersListCleanupIntervalTimerHandle);
    this.trackedWinnersList.splice(0);
  }

  private getTrackedWinnersList(): ITrackedWinner[] {
    return this.isWinnersTrackingFrozen ? this.frozenTrackedWinnersList : this.trackedWinnersList;
  }

  private notifyWinnersTracked(winners: ITrackedWinner[]): void {
    if (!this.isWinnersTrackingFrozen) {
      winners.forEach((winner) => {
        if (this.winnerCollector.length > 0) {
          if (winner.gameName !== this.winnerCollector[0].gameName || winner.clubName !== this.winnerCollector[0].clubName) {
            clearTimeout(this.winnerCollectorTimeout);
            this.enqueueAlertForCollatedWinnerNotification(this.winnerCollector);
            this.winnerCollector = [];
          }
        }
        this.collectWinner(winner);
        this.onWinnerTrackedObservable.next(winner);
      });
    }
  }

  private collectWinner(winner: ITrackedWinner) {
    this.winnerCollector.push(winner);
    clearTimeout(this.winnerCollectorTimeout);
    this.winnerCollectorTimeout = window.setTimeout(() => {
      this.enqueueAlertForCollatedWinnerNotification(this.winnerCollector);
      this.winnerCollector = [];
    }, 2000);
  }

  private async enqueueAlertForCollatedWinnerNotification(winners: ITrackedWinner[]): Promise<void> {
    const prizeType = winners.find((winner) => winner.prizeName != null && winner.prizeName.includes('Jackpot')) ? 'Jackpot' : 'prize';
    const sweetAlertOptions = await this.getSweetAlertOptionsForCollatedWinnerNotification(winners, prizeType);

    this.enqueueAlert(sweetAlertOptions, prizeType);
  }

  private enqueueAlert(sweetAlertOptions: SweetAlertOptions, prizeType: string) {
    const refocus = true;
    this.sweetAlertService.enqueue(sweetAlertOptions, refocus);

    if (this.userPreferences.jackpotNotificationSound) {
      this.playSoundDependingOnWinnerType(prizeType);
    }
  }

  private async getSweetAlertOptionsForCollatedWinnerNotification(winners: ITrackedWinner[], prizeType: string): Promise<SweetAlertOptions> {
    const TRACKED_WINNER_NOTIFICATION_TIMEOUT_MILLISECONDS = 30000;
    const winnerStrings = await Promise.all(winners.map((winner) => this.getSweetAlertMessageTextForTrackedWinnerNotification(winner)));
    return {
      title: this.$lang.game.winners.popup.title,
      html: winnerStrings.join('<br />'),
      type: 'info',
      toast: true,
      position: 'top-right',
      timer: TRACKED_WINNER_NOTIFICATION_TIMEOUT_MILLISECONDS,
      showCancelButton: true,
      cancelButtonText: this.$lang.chat.alertWords.dismissButton,
      customClass: this.getWinnerAlertCssClassName(prizeType)
    };
  }

  private getWinnerAlertCssClassName(prizeType: string): string {
    if (this.userPreferences.country.toLowerCase() !== 'uk') {
      switch (prizeType) {
        case 'Jackpot': {
          return 'jackpot-winner-modal';
          break;
        }
        default: {
          return 'prize-winner-modal';
          break;
        }
      }
    }
    return 'prize-winner-modal';
  }

  private playSoundDependingOnWinnerType(prizeType: string) {
    switch (prizeType) {
      case 'Jackpot': {
        this.soundService.play(Sounds.JACKPOT_WINNER);
        break;
      }
      default: {
        break;
      }
    }
  }

  private async getSweetAlertMessageTextForTrackedWinnerNotification(winner: ITrackedWinner): Promise<string> {
    let gameName = this.gameLocalisation.getLocalGameName(winner.gameName, winner.skinName);
    const prizeType: string = winner.prizeName && winner.prizeName.toLowerCase() !== 'prize' ? winner.prizeName : '';
    if (winner.clubName && winner.clubName !== gameName) {
      const hasSingleActiveClub = await this.gameConfigurationService.gameHasSingleActiveClub(winner.gameName);
      if (!hasSingleActiveClub) {
        gameName = `${gameName} (${winner.clubName})`;
      }
    }

    const message: string = (prizeType !== '') ? this.$lang.game.winners.popup.extendedMessage : this.$lang.game.winners.popup.message;
    return message.format({
      username: winner.username,
      prize: this.prizeHelper.formatPrize(winner.prizeInPence),
      gameName,
      prizeType
    });
  }

  private getMaxTrackedWinnersListSize(): number {
    return this.configurationService.getConfigurationItem(ConfigurationKey.MAX_WINNERS_LIST_SIZE);
  }

  private initialiseTrackedWinnersListCleanupIntervalTimer(): void {
    this.trackedWinnersListCleanupIntervalTimerHandle = window.setInterval(this.cleanupTrackedWinnersList.bind(this), TRACKED_WINNERS_LIST_CLEANER_INTERVAL_MILLISECONDS);
  }

  private cleanupTrackedWinnersList(): void {
    const numberOfTrackedWinnersToDrop = this.trackedWinnersList.length - this.maxTrackedWinnersListSize;
    if (numberOfTrackedWinnersToDrop > 0) {
      const trackedWinnersList = this.getTrackedWinnersList();
      trackedWinnersList.splice(0, numberOfTrackedWinnersToDrop);
    }
  }
}

export { ITrackedWinner, IWinnersTrackerService, WinnersTrackerService };



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/components/game/_domain/modules/winners-tracker-service/winners-tracker-service.ts